import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';
import { getApiErrorMessage } from 'src/services/api/utils';
import { reconcileCoapplicantCollection } from 'src/services/api/leads/coapplicants';

type IUpdateAssociatedLeadsResponse = {
    leads: ILead[];
}

/**
 * When specified, lead_uuids should either be empty (to remove all leads from the coapplicant group) or include the current lead UUID.
 */
type IUpdateAssociatedLeadsRequest = {
    group_id: number;
    lead_uuids: string[];
}

export const useUpdateAssociatedLeads = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IUpdateAssociatedLeadsRequest
    >(async (payload) => {
        const path = `${GET_LEADS_PATH}/coapplicants/group/${payload.group_id}`;

        const res = await apiClient(path, {
            method: 'post',
            json: {
                lead_uuids: payload.lead_uuids,
            },
        }).json<IUpdateAssociatedLeadsResponse>();
        return res.leads;
    }, {
        onSuccess: (data, payload) => {
            const groupId = payload.group_id;
            const returnedLeads = data ?? [];
            const cachedLeads = queryClient.getQueryData<ILead[]>(queryKeys.leads);
            const reconciled = reconcileCoapplicantCollection(
                cachedLeads,
                returnedLeads,
                new Set([groupId]),
            );
            queryClient.setQueryData<ILead[]>(queryKeys.leads, reconciled);
            const cachedByUuid = new Map(cachedLeads?.map((lead) => [lead.uuid, lead]));
            reconciled.forEach((lead) => {
                if (cachedByUuid.get(lead.uuid) !== lead) {
                    queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], lead);
                }
            });

            queryClient.invalidateQueries(queryKeys.conversations);

            addNotification('Coapplicants group updated', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to update coapplicants group';
            addNotification(errorMessage, 'error');
        }
    });
};
